import React from 'react';
import { TemplateType } from '../TemplateType';
import usePdfContent, { PdfContentParams } from '../usePdfContent';

import Grid from '@material-ui/core/Grid';

import PdfContent from 'apps/Pdf/components/common/PdfContent';
import Title from 'apps/Pdf/components/common/Title';
import H1 from 'apps/Pdf/components/common/H1';
import H2 from 'apps/Pdf/components/common/H2';
import H3 from 'apps/Pdf/components/common/H3';
import P from 'apps/Pdf/components/common/P';
import Spacer from 'apps/Pdf/components/common/Spacer';
import Article from 'apps/Pdf/components/common/Article';
import PageBreak from 'apps/Pdf/components/common/PageBreak';
import Signature from 'apps/Pdf/components/Signature/Signature';
import Loading from 'apps/Pdf/components/common/Loading';
import PersonalData from 'apps/Pdf/templates/MainContract/components/PersonalData';
import CompanyData from 'apps/Pdf/templates/MainContract/components/CompanyData';
import BankAccounts from 'apps/Pdf/templates/MainContract/components/BankAccounts';

import { PersonType } from 'enums';
import { MainContractContent } from './MainContractInterface';
import { ContractPhysicalData, ContractLegalData } from 'stores/workflowStores/workflowPersonalDataStore/model';
import useGeneralStyles from 'apps/Pdf/styles/general';
import useColorStyles from 'apps/Pdf/styles/colors';
import { PdfCategory } from '../Interfaces';

const MainContract = (params: PdfContentParams) => {
  const styles = useGeneralStyles();
  const pdfContent = usePdfContent<MainContractContent>({ templateType: TPL_MAIN_CONTRACT, category: PdfCategory.SIGNATURE, ...params });
  const colors = useColorStyles({ code: pdfContent?.data?.header.signedVia || '' });

  const data = pdfContent?.data ? pdfContent.data : ({} as MainContractContent);
  const legal = pdfContent?.data && data.clientData.personType === PersonType.LEGAL;

  return pdfContent ? (
    <PdfContent
      errors={pdfContent?.errors}
      className={`${styles.numberedHeadings} ${colors.root}`}
      onload
      afterLoad={params.afterLoad}
    >
      <Title>Rámcová smlouva o poskytování investiční služby</Title>
      <P className="tighter">
        Tato Rámcová smlouva o poskytování investičních služeb (dále jen „Rámcová smlouva“) byla uzavřena mezi:
      </P>

      <H2>Zákazník:</H2>

      {legal ? (
        <CompanyData data={data.clientData as ContractLegalData} />
      ) : (
        <PersonalData data={data.clientData as ContractPhysicalData} />
      )}

      <H3 className="inLines">Bankovní spojení</H3>

      <BankAccounts legal={legal} data={data.clientData.bankData.bankAccounts} />

      {legal && (
        <>
          <Spacer variant="half" />
          <H2>Zástupce zákazníka:</H2>
          {'representative' in data.clientData && <PersonalData data={data.clientData.representative} />}
        </>
      )}

      <Spacer variant="half" />

      <H2>WOOD RI:</H2>
      <P>
        <strong>WOOD Retail Investments a.s.</strong>, Jihlavská 1558/21, Michle, 140 00 Praha 4, IČO 17818834, zapsaná
        v obchodním rejstříku vedeném Městským soudem v Praze, oddíl B, vložka 27805, zastoupená{' '}
        <strong>Radimem Krejčím, předsedou představenstva</strong>.
      </P>
      <Grid container className={styles.regularText}>
        <Grid item xs={4} className={styles.gray}>
          Číslo účtu pro investice v {data.currency}:
        </Grid>
        <Grid item xs={3} className={styles.bold}>
          {data.woodBankAccount}
        </Grid>
        <Grid item xs={2} className={styles.gray}>
          Vedený u:
        </Grid>
        <Grid item xs={3}>
          Fio Banka a.s.
        </Grid>
      </Grid>
      <P>(WOOD RI a Zákazník společně dále též „Smluvní strany“ a jednotlivě jako „Smluvní strana“)</P>

      <div className={`${styles.twoColumns} ${styles.lines}`}>
        <Grid container>
          <Grid item xs={4}>
            Investiční zprostředkovatel:
          </Grid>
          <Grid item xs={8} className={styles.value}>
            {data.header.intermediaryCompany.companyName}
          </Grid>

          {data.header.advisor.role === 'DEPUTY' && (
            <>
              <Grid item xs={4}>
                Vázaný zástupce:
              </Grid>
              <Grid item xs={8} className={styles.value}>
                {data.header.partnerCompany.companyName}
              </Grid>
            </>
          )}
          <Grid item xs={4}>
            {data.header.advisor.role === 'DEPUTY' || data.header.advisor.role === 'DIRECT'
              ? 'Poradce'
              : 'Vázaný zástupce'}
            :
          </Grid>
          <Grid item xs={8} className={`${styles.bold} ${styles.value}`}>
            {data.header.advisor.firstname} {data.header.advisor.lastname}
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={4}>
            Osobní kód:
          </Grid>
          <Grid item xs={8} className={styles.value}>
            {data.header.advisor.code}
          </Grid>
          <Grid item xs={4}>
            Telefon:
          </Grid>
          <Grid item xs={8} className={styles.value}>
            {data.header.advisor.contacts.phone}
          </Grid>
          <Grid item xs={4}>
            E-mail:
          </Grid>
          <Grid item xs={8} className={styles.value}>
            {data.header.advisor.contacts.email}
          </Grid>
        </Grid>
      </div>

      <P>
        Následující smluvní ujednání, která zahrnují níže uvedené produktové smlouvy, tvoří spolu s obchodními
        podmínkami („<strong>Podmínky</strong>“), sazebníkem poplatků („
        <strong>Sazebník služeb</strong>“), seznamem bankovních účtů WOOD RI („
        <strong>Seznam bankovních účtů</strong>“) a reklamačním řádem („
        <strong>Reklamační řád</strong>“) tuto Rámcovou smlouvu, na základě které mohou Smluvní strany uzavírat finanční
        transakce a WOOD RI poskytovat Zákazníkovi Investiční služby. Pojmy a slovní spojení používané v Rámcové
        smlouvě, které nejsou Rámcovou smlouvou definovány, mají význam definovaný v Podmínkách.
      </P>
      <H1 numbered>Klasifikace zákazníka</H1>
      <H2 numbered>WOOD RI zařazuje Zákazníka do kategorie:</H2>
      <Article>
        <strong>Zákazník, který není profesionální</strong> (zákazník s nejvyšší zákonnou ochranou).
      </Article>
      <H1 numbered>Prohlášení zákazníka</H1>
      <H2 numbered>Seznámení s doprovodnými dokumenty</H2>
      <Article numbered>
        Zákazník výslovně prohlašuje, že byl seznámen s <strong>Podmínkami</strong> a jejich významem (včetně
        informačních dokumentů, na které odkazují), a také se <strong>Sazebníkem služeb</strong>,{' '}
        <strong>Seznamem bankovních účtů</strong> a <strong>Reklamačním řádem</strong> v dostatečném předstihu před
        uzavřením Rámcové smlouvy.
      </Article>
      <Article numbered>
        Zákazník výslovně prohlašuje, že si je vědom skutečnosti, že za poskytnuté služby obdrží WOOD RI odměnu podle
        Sazebníku služeb platného po dobu trvání Smlouvy v rozhodné době.
      </Article>
      <H2 numbered>Seznámení se se skutečnostmi vyžadovanými právními předpisy</H2>
      <Article numbered>
        Zákazník prohlašuje, že si je vědom všech skutečností, které pro něho vyplývají ze zařazení do výše uvedené
        kategorie zákazníků. Zákazník dále prohlašuje, že byl společností WOOD RI prostřednictvím{' '}
        <strong>Podmínek</strong> a dokumentu <strong>Pravidla klasifikace Zákazníků</strong> seznámen a poučen o právu
        požádat o přeřazení do jiné kategorie, pokud splňuje legislativou stanovené podmínky.
      </Article>
      <Article numbered>
        Zákazník výslovně prohlašuje, že se seznámil s dokumenty{' '}
        <strong>Strategie realizace pokynů za nejlepších podmínek</strong> a{' '}
        <strong>Opatření k zamezení střetu zájmů a manipulace s trhem</strong> a vyslovuje s nimi svůj souhlas.
      </Article>
      <Article numbered>
        Zákazník prohlašuje, že byl společností WOOD RI seznámen s režimem ochrany majetku zákazníka a informován o
        možných rizicích, která mohou být spojena s požadovanou Investiční službou nebo Investičním nástrojem, a o
        možných zajištěních proti nim prostřednictvím článků 5 a 6 části C Podmínek a dokumentu{' '}
        <strong>
          Informace o investičních službách, investičních nástrojích a rizicích spojených s investováním do těchto
          investičních nástrojů
        </strong>
        .
      </Article>
      <Article numbered>
        Zákazník potvrzuje, že byl informován o podmínkách poskytování náhrad z Garančního fondu a také o způsobu
        uplatňování nároku na výplatu náhrady prostřednictvím dokumentu <strong>Informace o Garančním fondu</strong>.
      </Article>
      <H2 numbered>Upozornění na některé právní povinnosti</H2>
      <Article numbered>
        <strong>Zákaz manipulace s trhem</strong> - Zákazník prohlašuje, že bere na vědomí, že v souvislosti s
        investováním do Investičních nástrojů je zakázána manipulace s trhem a zakázáno využívání důvěrných, resp.
        Vnitřních informací tak, jak je definují <strong>Podmínky</strong> a MAR.
      </Article>
      <Article numbered>
        <strong>Daňové povinnosti</strong> - Zákazník prohlašuje, že bere na vědomí a souhlasí s tím, že výplaty výnosů
        z držby nebo nakládání s Investičními nástroji zpravidla podléhají daním a poplatkům. Pokud z příslušných
        předpisů nebo Rámcové smlouvy nevyplývá jinak, nezajišťuje WOOD RI pro Zákazníka úhradu žádných daní nebo jejich
        zajištění v souvislosti s poskytováním Investičních služeb.
      </Article>
      <Article numbered>
        <strong>Práva a povinnosti spojená s vlastnictvím Investičních nástrojů</strong> - Zákazník bere na vědomí, že
        WOOD RI neodpovídá za plnění oznamovací povinnosti Zákazníka či jiných práv a povinností Zákazníka vyplývajících
        z vlastnictví Investičních nástrojů, resp. z podílu na hlasovacích právech. Zákazník bere na vědomí, že
        specifické povinnosti vyplývající z vlastnictví Investičních nástrojů mu mohou vzniknout jak podle právního řádu
        ČR, tak i zahraničních právních řádů.
      </Article>
      <Article numbered>
        <strong>Zaznamenávání komunikace</strong> - Zákazník prohlašuje, že bere na vědomí, že WOOD RI pořizuje pro
        účely své evidence záznamy o všech Pokynech, které od Zákazníka obdržel. Zákazník prohlašuje, že si je vědom
        skutečnosti, že společnost WOOD RI je povinna pořizovat a archivovat nahrávky telefonické a elektronické
        komunikace a záznamy z osobních jednání se Zákazníkem, které probíhají v souvislosti s poskytováním Investičních
        služeb, a to i v případě, že k poskytnutí Investiční služby nedojde.Zákazník dále bere podpisem Smlouvy na
        vědomí, že tyto záznamy mohou být na základě zákona nebo příslušných předpisů daného Převodního místa
        zpřístupněny příslušným kontrolním orgánům a může jich být, v případě soudního sporu, použito jako důkazních
        prostředků u soudu.Kopie komunikace specifikované výše je archivována po dobu 5 let, nebo 7 let, pokud tak
        rozhodne ČNB (tato lhůta se počítá od okamžiku, kdy příslušná komunikace proběhla).Zákazník má právo požadovat
        zpřístupnění nahrávek telefonické a elektronické komunikace a záznamů z osobních jednání v přiměřené lhůtě v
        rozsahu, v němž se vztahují k (potenciálním) transakcím s Investičními nástroji. Žádost musí být učiněna v
        písemné formě a musí specifikovat relevantní obchody či alespoň časové období, za které má být kopie komunikace
        a záznamů poskytnuta. Za zpřístupnění kopií nahrávek a záznamů bude Zákazníkovi účtován poplatek v souladu se
        Sazebníkem služeb.
      </Article>
      <Article numbered>
        <strong>Jednání na vlastní účet</strong> - Zákazník potvrzuje společnosti WOOD RI, že při uzavírání Rámcové
        smlouvy jedná na vlastní účet.
      </Article>
      <Article numbered>
        <strong>Trvalý pobyt ve vybraných jurisdikcích</strong> - Zákazník bere na vědomí, že pokud společnosti WOOD RI
        před uzavřením této Rámcové smlouvy nebo v průběhu jejího trvání zatajil, že je tzv. „U.S. person“ ve smyslu
        právního předpisu Spojených států amerických Foreign Account Tax Compliance Act (FATCA) nebo Nařízení S
        (Regulation S) na základě amerického Zákona o cenných papírech (Securities Act) z roku 1933, případně má trvalý
        pobyt nebo sídlo v Kanadě, Spojeném království Velké Británie, Austrálie, Japonsku nebo jakékoli jiné
        jurisdikci, kde je uzavření této Rámcové smlouvy zakázáno nebo omezeno, je společnost WOOD RI oprávněna Rámcovou
        smlouvu vypovědět a Zákazník bere na vědomí, že WOOD RI neodpovídá za finanční ztrátu a náklady, které
        Zákazníkovi vzniknou v souvislosti s ukončením Rámcové smlouvy.
      </Article>
      <Article numbered>
        <strong>Prohlášení o přijmu</strong> - Zákazník rovněž prohlašuje, že společně s touto Rámcovou smlouvou provedl
        prohlášení o skutečném vlastnictví připisovaného, poukazovaného nebo vypláceného příjmu („Prohlášení o příjmu“).
        Zákazník se zavazuje neprodleně informovat WOOD RI o jakýchkoliv změnách údajů v Prohlášení o příjmu. V případě
        uvedení nepravdivých, neúplných nebo zkreslených údajů v Prohlášení o příjmu či v případě porušení své
        informační povinnosti o změnách v <strong>Prohlášení o příjmu</strong> je Zákazník povinen nahradit společnosti
        WOOD RI veškerou škodu či uloženou sankci, která v důsledku jednání či opomenutí Zákazníka vznikla.
      </Article>
      <Article numbered>
        <strong>Spolupráce při informování třetích stran – archivace dokumentace</strong> - Zákazník prohlašuje, že si
        je vědom skutečnosti, že IRS (daňový úřad USA) či jiné entity mohou požádat WOOD RI o doložení originálů
        dokumentace Zákazníka. Zákazník se proto zavazuje archivovat veškeré originály dokumentů poskytnutých
        společnosti WOOD RI formou elektronické kopie, zejména pak formulář W-8BEN.
      </Article>
      <H2 numbered>Dohoda o některých aspektech poskytování Investičních služeb</H2>
      <Article numbered>
        V případě, že Zákazník podá Pokyn a/nebo WOOD RI provede Investiční rozhodnutí, které vyústí ke vzniku limitního
        pokynu ve vztahu k Investičním nástrojům přijatým k obchodování v Obchodním systému, a tento limitní pokyn
        nebude okamžitě proveden za převažujících tržních podmínek nebo předán do Obchodního systému, Zákazník výslovně
        požaduje, aby WOOD RI nezveřejňovala takový limitní pokyn.
      </Article>
      <Article numbered>
        Zákazník souhlasí s tím, že WOOD RI může Pokyn a/nebo Investiční rozhodnutí provádět mimo Obchodní systém.
      </Article>
      <Article numbered>
        Zákazník uděluje svůj výslovný souhlas, aby mu byly informace poskytovány i na jiném trvalém nosiči dat než na
        papíru, pokud to platné právní předpisy připouštějí. Zákazník se v tomto rozsahu výslovně vzdává práva na
        zasílání poskytovaných dokumentů poštou. Informační povinnosti společnosti WOOD RI vůči Zákazníkovi mohou být
        plněny i v jiné než v listinné podobě, tj. elektronicky (např. emailem), přičemž Zákazník souhlasí s tím, že
        elektronicky (např. emailem), telefonicky nebo jinými prostředky komunikace na dálku (např. prostřednictvím
        internetu) může mezi ním a společností WOOD RI probíhat i jejich vzájemná komunikace při poskytování služeb
        podle této Rámcové smlouvy.
      </Article>
      <H2 numbered>Upozornění na některá vybraná rizika související s investováním a Investičními službami</H2>
      <Article numbered>
        Zákazník prohlašuje, že bere na vědomí, že historická data o výkonnosti Investičních nástrojů nezaručují výši
        budoucí výkonnosti.
      </Article>
      <Article numbered>
        WOOD RI nenese odpovědnost za rozhodnutí Zákazníka související s poskytováním Investičních služeb podle této
        Rámcové smlouvy.
      </Article>
      <Article numbered>
        Zákazník prohlašuje, že bere na vědomí, že WOOD RI nenese odpovědnost za škodu způsobenou Zákazníkovi správným
        provedením Pokynu podaného Zákazníkem v souladu s Rámcovou smlouvou.
      </Article>
      <H2 numbered>Povinnost Zákazníka poskytovat údaje pravdivě, úplně a nezkresleně</H2>
      <Article numbered>
        Zákazník prohlašuje, že veškeré údaje, které poskytl společnosti WOOD RI v souvislosti s Rámcovou smlouvou a s
        poskytováním Investičních služeb, jsou pravdivé, úplné a nezkreslené a že nezamlčuje či nezatajuje informace
        důležité pro řádné plnění Rámcové smlouvy. Zákazník se dále zavazuje tyto údaje pravidelně kontrolovat a při
        změně neprodleně, nejpozději však do 30 dní, aktualizovat prostřednictvím komunikačních kanálů dohodnutých se
        společností WOOD RI.
      </Article>
      <Article numbered>
        Zákazník je zejména povinen kontrolovat a aktualizovat údaje nezbytné pro vhodné poskytování Investičních služeb
        Zákazníkovi (jako jsou např. finanční situace, investiční cíle či ochota riskovat), veškeré kontaktní údaje a
        také další informace vyžadované od Zákazníka v důsledku požadavků stanovených v právních předpisech (zejm. v
        rámci boje proti praní špinavých peněz).
      </Article>
      <Article numbered>
        V průběhu trvání smluvního vztahu může vzniknout i povinnost sdělení dodatečných údajů ze strany Zákazníka. Při
        nesplnění těchto povinnosti si WOOD RI vyhrazuje právo Rámcovou smlouvu vypovědět.
      </Article>
      <Article numbered>
        WOOD RI neodpovídá za jakoukoli újmu, náklady či jiné výdaje způsobené Zákazníkovi nebo kterékoli třetí osobě v
        důsledku nepravdivosti, neúplnosti či zkreslenosti údajů poskytnutých Zákazníkem.
      </Article>
      <H1 numbered>Plná moc</H1>
      <Article numbered levelUp>
        Touto Rámcovou smlouvou Zákazník společnost WOOD RI pověřuje, aby jej zastupovala při jednáních s třetími
        stranami, fyzickými nebo právnickými osobami, které mají vztah k předmětu této Rámcové smlouvy, a WOOD RI svým
        podpisem toto oprávnění poskytnuté Zákazníkem výslovně přijímá.
      </Article>
      <H1 numbered>Další ujednání</H1>
      <Article numbered levelUp>
        Společnost WOOD RI je oprávněna kdykoli měnit Seznam bankovních účtů a Reklamační řád. O změně Seznamu
        bankovních účtů a Reklamačního řádu bude Zákazník informován způsobem stanoveným v Podmínkách.
      </Article>
      <Article numbered levelUp>
        WOOD RI si vyhrazuje právo měnit Podmínky a Sazebník služeb. Způsob oznámení změn v Podmínkách a Sazebníku
        služeb i možnost Rámcovou smlouvu z důvodu takové změny vypovědět, jsou specifikovány v Podmínkách.
      </Article>
      <Article numbered levelUp>
        Rámcová smlouva může být ukončena:
        <Article numbered>písemnou dohodou Smluvních stran ke dni stanovenému v takové dohodě;</Article>
        <Article numbered>
          výpovědí kteroukoli Smluvní stranou bez udání důvodu s jednoměsíční výpovědní dobou, která začíná běžet prvním
          dnem měsíce následujícího po měsíci, v němž byla výpověď doručena druhé Smluvní straně; a
        </Article>
        <Article numbered>
          odstoupením od smlouvy v případech výslovně upravených v Rámcové smlouvě nebo Podmínkách a dále v případech
          stanovených Občanským zákoníkem nebo zvláštními právními předpisy.
        </Article>
      </Article>
      <Article numbered levelUp>
        Jednotlivé produktové smlouvy mohou být ukončeny obdobně jako Rámcová smlouva. Při ukončení jednotlivé
        produktové smlouvy dochází také k zániku takových dalších produktových smluv, jejichž právní nebo faktická
        existence je výpovědí příslušné produktové smlouvy vyloučena. Ukončením jednotlivé produktové smlouvy Rámcová
        smlouva nezaniká, s výjimkou případu, kdy ukončením jednotlivé produktové smlouvy dochází k zániku závazku
        společnosti WOOD RI poskytovat Investiční služby Zákazníkovi.
      </Article>
      <PageBreak />
      <Title>I. SMLOUVA O OBHOSPODAŘOVÁNÍ PORTFOLIA</Title>
      <P>
        v souladu s ustanovením § 1746 odst. 2 a násl. zákona č. 89/2012 Sb., občanského zákoníku, v platném a účinném
        znění, a podle ustanovení § 4 odst. 2 písm. d) zákona č. 256/2004 Sb., zákona o podnikání na kapitálovém trhu, v
        platném a účinném znění (dále jen „<strong>Obhospodařovatelská smlouva</strong>“)
      </P>
      <H1 numbered>Předmět smlouvy</H1>
      <Article numbered levelUp>
        Předmětem této smlouvy je závazek společnosti WOOD RI investovat majetek Zákazníka
        tvořený Investičními nástroji a peněžními prostředky určenými k investování do Investičních nástrojů (dále jen
        „Portfolio“), a to na základě pokynů definovaných Zákazníkem prostřednictvím aplikace Edward za podmínek stanovených v této
        smlouvě (dále jen “Služba Edward”), a povinnost Zákazníka zaplatit společnosti WOOD RI za poskytnuté
        služby odměnu dle Sazebníku služeb.
      </Article>
      <Article numbered levelUp>
        V rámci služby Edward je společnost WOOD RI zmocněna zastupovat Zákazníka vůči třetím osobám, a tedy
        jednat jménem Zákazníka nebo jménem svým na Zákazníkův účet a riziko.{' '}
      </Article>
      <Article numbered levelUp>
        Služba Edward nezahrnuje investiční, daňové ani právní poradenství.
      </Article>
      <Article numbered levelUp>
        V případě, že Zákazník využije investiční Službu Edward, bere Zákazník na vědomí a
        souhlasí s tím, že WOOD RI v rámci Služby Edward může do Portfolia takového Zákazníka zařazovat i
        tzv. Frakční podíly.
      </Article>
      <H1 numbered>Investiční strategie</H1>
      <Article numbered levelUp>
        Společnost WOOD RI nabídla Zákazníkovi vhodné investiční strategie na základě údajů, které Zákazník samostatně
        poskytl o svých odborných znalostech a zkušenostech, finančním zázemí, včetně schopnosti nést ztráty, a
        investičních cílech, včetně tolerance k riziku.
      </Article>
      <Article numbered levelUp>
        Zákazník může Portfolio rozdělit na dílčí částky a ohledně každé dílčí částky sledovat samostatnou investiční
        strategii s odlišnými parametry. WOOD RI může v rámci služby Obhospodařování Portfolia prostředky v průběhu času
        přesouvat (včetně deinvesticí a reinvesticí investičních nástrojů) mezi jednotlivými investičními strategiemi za
        účelem realizace jejich dílčích cílů, ale i cílů v rámci celkové strategie Zákazníka.
      </Article>
      <Article numbered levelUp>
        Zákazník tímto výslovně pověřuje a zmocňuje společnost WOOD RI obstarávat Obhospodařování Portfolia podle
        investiční strategie vypracované společností WOOD RI, na které se WOOD RI a Zákazník dohodli. Společnost WOOD RI
        je zejména pověřena v rámci investiční strategie v Obchodních systémech i mimo ně nakupovat, prodávat, směňovat,
        převádět nebo upisovat Investiční nástroje nebo s nimi jinak nakládat nebo uplatňovat práva z Investičních
        nástrojů, resp. provádět veškerá jiná opatření, která se jeví v souvislosti s Obhospodařováním Portfolia jako
        účelná.
      </Article>
      <Article numbered levelUp>
        Společnost WOOD RI je při poskytování služby Edward vázána investičními strategiemi dohodnutými se Zákazníkem.
        Nepodstatné nebo krátkodobé odchylky od limitů a kritérií investiční strategie se však nepovažují za porušení
        Smlouvy. Pokud dojde v důsledku kolísání trhu, Pokynů Zákazníka, převodu majetku na Peněžní
        účet a/nebo Účet cenných papírů nebo z jiného důvodu k podstatným odchylkám od investičních strategií, provede
        WOOD RI na základě dopředné instrukce Zákazníka vhodná opatření, aby bylo v přiměřené době zajištěno nebo obnoveno
        dodržování investičních strategií.
      </Article>
      <Article numbered levelUp>
        Zákazník tímto bere na vědomí, že při podání Pokynu k převodu peněžních prostředků na Bankovní účet Zákazníka
        mohou být zásadním způsobem negativně ovlivněny výnosy z Obhospodařování Portfolia.
      </Article>
      <Article numbered levelUp>
        Přijaté pokyny budou realizovány v následujícím Obchodním cyklu za obchodní cenu v daný obchodní den, ne za cenu
        v době zadání pokynu.
      </Article>
      <Article numbered levelUp>
        Pokud je součástí Investiční strategie Zajištění proti kurzovému riziku, Zákazník výslovně prohlašuje, že byl
        společností WOOD RI seznámen s podstatou, právní povahou a riziky Zajištění proti kurzovému riziku.
      </Article>
      <H1 numbered>Podávání zpráv</H1>
      <Article numbered levelUp>
        WOOD RI poskytuje jednou měsíčně Zákazníkovi výpis z Účtu za bezprostředně předcházející období, a to pro každou
        investiční strategii, která je součástí Obhospodařovatelské smlouvy.
      </Article>
      <Article numbered levelUp>
        Výpis z Účtu podle předchozího článku bude obsahovat zejména přesný popis složení Portfolia s údaji o tržních
        hodnotách příslušných Investičních nástrojů, vývoj celkové hodnoty Portfolia ve sjednané měně, jakož i celkovou
        výši Poplatků za rozhodné období.
        <br />
        <br />
        WOOD RI bude v souladu s platnou právní úpravou informovat Zákazníka, pokud se celková hodnota Portfolia oceněná
        na začátku každého vykazovaného období, tedy kalendářního měsíce, sníží o 10 % a poté o násobky 10 %.
        <br />
        <br />
        Pro tyto účely se použije následující postup výpočtu: tržní hodnota Investičních nástrojů na Účtu cenných papírů
        na začátku sledovaného období (dále jen „počáteční hodnota Investičních nástrojů“) se porovná s aktuální tržní
        hodnotou Investičních nástrojů na Účtu cenných papírů. Aby byly zohledněny také transakce od počátku sledovaného
        období, připočte se k počáteční hodnotě Investičních nástrojů čistý přírůstek peněžních prostředků na Peněžním
        účtu.
      </Article>
      <H1 numbered>Odměna společnosti WOOD RI a další náklady zákazníka spojené s investicí</H1>
      <Article numbered levelUp>
        Manažerský poplatek
        <br />
        <br />
        Odměna formou manažerského poplatku se vypočítá jako procentní sazba z tržní hodnoty Portfolia určené na denní
        bázi za kalendářní měsíc a je splatná zpětně za každý měsíc. Odměna bude hrazena z peněžních prostředků
        Zákazníka na Peněžním účtu. Zákazník tímto uděluje společnosti WOOD RI souhlas s inkasem odměny z Peněžního
        účtu, případně s prodejem Investičních nástrojů z Portfolia Zákazníka za účelem inkasa Poplatků. Pokud je Účet
        zrušen v průběhu měsíce, odměna se vypočítá a provede se její inkaso po vypořádání prodeje všech Investičních
        nástrojů a před uvolněním zůstatku Zákazníkovi.
      </Article>
      <Article numbered levelUp>
        Další poplatky hrazené Zákazníkem
        <br />
        <br />S ohledem na povahu poskytované služby může WOOD RI vyžadovat kromě manažerského poplatku i jiné typy
        poplatků (jako např. vstupní poplatek, výstupní poplatek, výkonnostní poplatek atd.). Všechny poplatky, které
        budou pro Zákazníka s ohledem na typ poskytované služby ze strany WOOD RI relevantní, jsou uvedeny v Sazebníku
        služeb.
      </Article>
      <Article numbered levelUp>
        Další náklady a výdaje hrazené Zákazníkem
        <br />
        <br />
        Zákazník je povinen uhradit náklady a výdaje, které společnosti WOOD RI oprávněně vzniknou při poskytování
        Obhospodařování Portfolia nebo úkonu s tím spojeného, nebo při plnění jejích povinností vyplývajících z právních
        předpisů. Výše takových nákladů a výdajů bude dána v Sazebníku služeb, bude-li možno odhadnout je předem. WOOD
        RI se bude vždy snažit minimalizovat vzniklé náklady a výdaje.
      </Article>
      <Article numbered levelUp>
        Informace o snížení zisku / zvýšení ztráty v případě obchodů s ETF
        <br />
        <br />V rámci Obhospodařování Portfolia jsou nakupovány zejména cenné papíry kolektivního investování a tzv.
        Exchange Traded Funds (dále jen „ETF“). Při nákupu ETF vznikají zpravidla transakční náklady ve formě rozdílů
        mezi nákupním a prodejním kurzem (tzv. spread) či poplatků účtovaných tvůrcem trhu. Náklady jsou uhrazeny v
        rámci vypořádání daného obchodu realizovaného společností WOOD RI a nejsou mu účtovány zvlášť.
      </Article>
      <Article numbered levelUp>
        Daňové povinnosti spojené s Investicí
        <br />
        <br />V souvislosti s nákupem nebo prodejem Investičních nástrojů na účet Zákazníka může vzniknout Zákazníkovi
        daňová povinnost. Daně nejsou zahrnuty v manažerském poplatku a hradí je sám Zákazník.
      </Article>
      <H1 numbered>Investiční pobídky</H1>
      <Article numbered levelUp>
        Existuje možnost, že WOOD RI obdrží v rámci Obhospodařování Portfolia provize, poplatky a jiná peněžitá plnění,
        jakož i nepeněžité výhody (dále jen „<strong>pobídky</strong>“) od investičních fondů, emitentů Investičních
        nástrojů a jiných třetích osob.
      </Article>
      <Article numbered levelUp>
        WOOD RI připíše obdržené pobídky ve prospěch Peněžního účtu Zákazníka. Pokud je připsání ve prospěch Peněžního
        účtu Zákazníka vyloučeno, protože se nejedná o peněžité plnění, nebo není možné z jiných důvodů, WOOD RI pobídku
        přijme pouze tehdy, pokud pobídka nebude v rozporu s právními předpisy platnými a účinnými v dané době a s
        řádným Obhospodařováním Portfolia v nejlepším zájmu Zákazníka. WOOD RI zveřejní přijaté pobídky v rozsahu a
        způsobem požadovaným v té době platnými a účinnými právními předpisy.
      </Article>
      <Article numbered levelUp>
        WOOD RI může ze svého majetku poskytovat plnění Investičním zprostředkovatelům či Vázaným zástupcům za prokázané
        poskytnutí příležitosti k uzavření nebo za zprostředkování Smlouvy, a to ve výši podle hodnoty peněžních
        prostředků investovaných Zákazníkem. Zákazníkovi z toho nevznikají žádné další náklady, protože tato plnění se
        neposkytují z majetku Zákazníka.
      </Article>
      <H1 numbered>Daňové záležitosti</H1>
      <Article numbered levelUp>
        Společnost WOOD RI není povinna provádět pro Zákazníka daňově optimalizované Obhospodařování Portfolia.
      </Article>
      <Article numbered levelUp>
        Na základě Obhospodařování Portfolia, zejména při rebalancování či změně investiční strategie, nebo při prodeji
        Investičních nástrojů z Portfolia Zákazníka za účelem inkasa Poplatků nebo v případě ukončení této
        Obhospodařovatelské smlouvy, mohou z obchodních transakcí vzniknout zdanitelné zisky nebo negativní daňové
        dopady pro Zákazníka.
      </Article>
      <Article numbered levelUp>
        Zákazník se s daňovými otázkami obrátí na nezávislého daňového poradce.
      </Article>
      <H1 numbered>Závěrečná ustanovení</H1>
      <Article numbered levelUp>
        Ustanovení o správě cizího majetku (§ 1400 a násl.) zákona č. 89/2012 Sb., občanského zákoníku, v platném a
        účinném znění, se na tuto Rámcovou smlouvu neuplatní.
      </Article>
      <Article numbered levelUp>
        Obhospodařovatelská smlouva nabývá účinnosti ke dni podpisu Rámcové smlouvy oběma Smluvními stranami.
      </Article>
      <PageBreak />

      <Title>II. CUSTODY SMLOUVA</Title>
      <P>
        v souladu s ustanovením § 1746 odst. 2 a násl. zákona č. 89/2012 Sb., občanský zákoník, v platném a účinném
        znění, a podle ustanovení § 4 odst. 3 písm. a) zákona č. 256/2004 Sb., zákon o podnikání na kapitálovém trhu, v
        platném a účinném znění (dále jen „<strong>Custody smlouva</strong>“),
      </P>
      <Article numbered topLevel>
        Předmětem Custody smlouvy je závazek společnosti WOOD RI zajišťovat úschovu a správu Investičních nástrojů ve
        vlastnictví Zákazníka a závazek Zákazníka zaplatit za poskytnuté služby společnosti WOOD RI odměnu dle Sazebníku
        služeb.
      </Article>
      <Article numbered topLevel>
        Podmínky úschovy a správy Investičních nástrojů se řídí platnými předpisy, rozhodnutími příslušných orgánů a
        obchodními podmínkami, jež se aplikují na příslušném trhu.
      </Article>
      <Article numbered topLevel>
        Při poskytování služeb Zákazníkovi podle Custody smlouvy bude WOOD RI:
        <Article numbered levelUp>
          vést evidenci Investičních nástrojů;
        </Article>
        <Article numbered levelUp>
          podle Pokynů Zákazníka a/nebo provedených Investičních rozhodnutí prodávat, směňovat, převádět nebo upisovat
          Investiční nástroje a připisovat výnosy z prodeje na příslušný Účet nebo odepisovat z Účtu kupní cenu včetně
          veškerých souvisejících transakčních nákladů a poplatků;
        </Article>
        <Article numbered levelUp>
          přijímat na účet Zákazníka a držet všechny Investiční nástroje, které WOOD RI obdrží na účet Zákazníka na
          základě Custody smlouvy;
        </Article>
        <Article numbered levelUp>
          činit jménem Zákazníka a na účet Zákazníka veškeré úkony, jaké jsou zapotřebí ve vztahu k přijímání plateb,
          příjmů nebo výnosů z Investičních nástrojů;
        </Article>
        <Article numbered levelUp>
          inkasovat veškeré úroky, dividendy nebo jiné příjmy spojené s Investičními nástroji;
        </Article>
        <Article numbered levelUp>
          vyměňovat Investiční nástroje v případě jejich výměny; a
        </Article>
        <Article numbered levelUp>
          poskytovat další služby, na nichž se WOOD RI se Zákazníkem dohodne.
        </Article>
      </Article>
      <Article numbered topLevel>
        Správa Investičních nástrojů nezahrnuje výkon práva účasti na řízení akciové společnosti (zejména právo hlasovat
        na valné hromadě), nedohodne-li se Zákazník se společností WOOD RI výslovně jinak.
      </Article>
      <Article numbered topLevel>
        Zákazník je povinen poskytnout společnosti WOOD RI součinnost potřebnou k plnění Custody smlouvy (např. podání
        Pokynu, udělení speciální plné moci, předložení dokladu o daňovém domicilu apod.).
      </Article>
      <Article numbered topLevel>
        Custody smlouva nabývá účinnosti ke dni podpisu Rámcové smlouvy oběma Smluvními stranami.
      </Article>
      <PageBreak />

      <Title>III. SMLOUVA O OBSTARÁNÍ A ZPROSTŘEDKOVÁNÍ KOUPĚ A PRODEJE INVESTIČNÍCH NÁSTROJŮ</Title>

      <P>
        v souladu s ustanovením § 2455 a násl. zákona č. 89/2012 Sb., občanský zákoník, v platném a účinném znění (dále
        jen "<strong>Komisionářská smlouva</strong>")
      </P>
      <ol className={styles.regularText}>
        <li>
          Předmětem Komisionářské smlouvy je závazek WOOD RI koupit nebo prodat Investiční nástroj pro Zákazníka, popř.
          obstarat pro něj koupi nebo prodej Investičních nástrojů, nebo vyvíjet činnost směřující ke koupi nebo prodeji
          Investičních nástrojů podle závazných Pokynů Zákazníka na straně jedné a závazek Zákazníka zaplatit za
          poskytnuté služby WOOD RI odměnu dle Sazebníku služeb.
        </li>
        <li>
          Nákup nebo prodej Investičních nástrojů bude WOOD RI provádět na základě závazných písemných nebo ústních
          Pokynů Zákazníka. Tyto Pokyny musí být určité a srozumitelné.
        </li>
        <li>
          Zákazníci využívající Internetovou aplikaci jsou oprávněni podávat Pokyny zásadně prostřednictvím Internetové
          aplikace. WOOD RI není povinna přijmout Pokyn podaný jinak než prostřednictvím Internetové aplikace.
        </li>
        <li>
          WOOD RI bude přijímat Pokyny k obstarání koupě nebo prodeje jen těch Investičních nástrojů, které jsou
          zobrazeny v Internetové aplikaci.
        </li>
        <li>
          Pokyny přijaté prostřednictvím Internetové aplikace budou realizovány v následujícím Obchodním cyklu, jedná-li
          se o Pokyn týkající se Investičního nástroje obchodovaného na Regulovaném trhu
        </li>
        <li>
          Ostatní pokyny přijaté prostřednictvím Internetové aplikace budou realizovány v souladu s emisními podmínkami
          daného Investičního nástroje.
        </li>
        <li>
          WOOD RI je oprávněna nákup nebo prodej Investičního nástroje uskutečnit i ze/do svého majetku nebo nákupem
          Investičních nástrojů od jiných svých Zákazníků.
        </li>
        <li>
          Další práva a povinnosti Zákazníka a WOOD RI vyplývající z Komisionářské smlouvy jsou stanoveny v Podmínkách.
        </li>
        <li>Komisionářská smlouva nabývá účinnosti ke dni podpisu Rámcové smlouvy.</li>
      </ol>

      <PageBreak />

      <Title>PROHLÁŠENÍ O SKUTEČNÉM VLASTNICTVÍ PŘIPISOVANÉHO, POUKAZOVANÉHO NEBO VYPLÁCENÉHO PŘÍJMU</Title>
      <P>Zákazník prohlašuje, že k datu podpisu:</P>
      <ol className={styles.regularText}>
        <li>
          je daňovým rezidentem výše uvedeného státu ve smyslu článku 4 příslušné smlouvy o zamezení dvojího zdanění
          anebo zákona č. 586/1992 Sb., o daních z příjmů;
        </li>
        <li>
          je skutečným vlastníkem veškerých příjmů připisovaných, poukazovaných či vyplácených v jeho prospěch
          společností WOOD Retail Investments a.s., Jihlavská 1558/21, Michle, 140 00 Praha 4, IČO 17818834 nebo jiné
          společnosti ze Skupiny WOOD RI (dále jen „Společnost“);
        </li>
        <li>
          veškeré tyto příjmy jsou dle daňového práva státu, jehož je daňovým rezidentem, považovány za jeho příjem; a
        </li>
        <li>
          veškeré tyto příjmy nejsou přiřaditelné stálé provozovně, pokud existuje, ve smyslu článku 5 příslušné smlouvy
          o zamezení dvojího zdanění.
        </li>
      </ol>
      <P>
        V případě, že by se výše uvedené skutečnosti změnily, zavazuje se Zákazník neprodleně o této skutečnosti písemně
        informovat Společnost.
      </P>
      <P>
        Zákazník se zavazuje nahradit Společnosti škodu anebo sankci vzniklou v důsledku uvedení údajů neodpovídajících
        skutečnosti a škodu anebo sankci způsobenou neposkytnutím informace o změnách ve výše uvedených údajích.
      </P>
      <P>
        Toto prohlášení je učiněno za účelem čerpání výhod plynoucích ze zákona č. 586/1992 Sb., o daních z příjmů, ve
        znění pozdějších předpisů, a z příslušné smlouvy o zamezení dvojího zdanění.
      </P>
      <PageBreak />

      <H1>Podpis WOOD RI</H1>
      <P>
        Na důkaz souhlasu s obsahem této Rámcové smlouvy připojují Smluvní strany své podpisy a zároveň prohlašují, že
        tato Rámcová smlouva byla uzavřena z jejich svobodné a vážné vůle.
      </P>
      <P>
        Za <strong>WOOD Retail Investments a.s. Radim Krejčí - Předseda představenstva</strong>
      </P>

      <Signature protocol={data.signedInfo} />
    </PdfContent>
  ) : params.preview ? (
    <Loading />
  ) : null;
};

const TPL_MAIN_CONTRACT: TemplateType = 'mainContract';

export { MainContract, TPL_MAIN_CONTRACT };
